/**
 * Commitment -> Cycles (f10.2), following the prototype's `cycles.html`.
 *
 * Every derived value on this screen -- label, closing month, the five
 * working-day dates and their `WD<n>` tags, the previous cycle -- is read from
 * the API's `derived` block. The screen computes none of them, so a change to
 * an offset in Configuration shows here on the next read (adr-13).
 *
 * The New cycle button follows the server's `capabilities.can_create`; the
 * server remains the boundary on `POST /api/cycles`.
 *
 * Not carried from the prototype (Q-f10.2-5): the Export button and the
 * Committed FY column -- no source need exports this list, and no committed
 * figure exists before f11/f12.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarDays, Plus } from 'lucide-react';

import type { CycleListItem } from '@/api/client';
import { useCycles } from '@/api/queries';

import { CycleStateBadge } from './CycleStateBadge';
import {
  MILESTONE_COLUMNS,
  columnTag,
  formatCycleDate,
  milestoneFor,
} from './cycleView';
import { NewCycleDialog } from './NewCycleDialog';

function PageHead({ onCreate }: { onCreate?: () => void }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Cycles</h1>
        <div className="page-sub">Monthly revenue commitment cycles · identified YYMM</div>
      </div>
      <div className="page-actions">
        {onCreate && (
          <button type="button" className="btn btn-primary" onClick={onCreate}>
            <Plus aria-hidden="true" /> New cycle
          </button>
        )}
      </div>
    </div>
  );
}

function StateCard({ icon, title, children }: { icon: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="empty">
          <div className="e-icon">{icon}</div>
          <h3>{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

function CycleRow({ cycle }: { cycle: CycleListItem }) {
  const { derived } = cycle;
  const target = `/cycles/${cycle.id}`;
  return (
    <tr>
      <td className="mono">
        <Link className="cell-link" to={target}>
          <b>{cycle.code}</b>
        </Link>
      </td>
      <td>
        <Link className="cell-link" to={target}>
          {derived.label}
        </Link>
      </td>
      <td>
        <CycleStateBadge state={cycle.state} />
      </td>
      <td>{derived.closing_month.label}</td>
      {MILESTONE_COLUMNS.map((column) => {
        const milestone = milestoneFor(derived, column.key);
        return (
          <td key={column.key} className="cycle-date">
            {milestone ? formatCycleDate(milestone.date) : '—'}
          </td>
        );
      })}
      <td>
        {derived.previous_cycle ? (
          <span className="mono">{derived.previous_cycle.code}</span>
        ) : (
          <span className="hint">—</span>
        )}
      </td>
    </tr>
  );
}

export function CyclesPage() {
  const [state, setState] = useState('');
  const [year, setYear] = useState('');
  const [creating, setCreating] = useState(false);
  const cycles = useCycles({
    state: state || undefined,
    year: year ? Number(year) : undefined,
  });

  const data = cycles.data;
  const canCreate = data?.capabilities.can_create ?? false;
  const openCreate = canCreate ? () => setCreating(true) : undefined;

  if (cycles.isError && !data) {
    return (
      <>
        <PageHead />
        <StateCard icon={<AlertTriangle aria-hidden="true" />} title="Cycles could not be loaded">
          <p>{cycles.error instanceof Error ? cycles.error.message : 'Try again shortly.'}</p>
        </StateCard>
      </>
    );
  }

  const items = data?.items ?? [];
  const filtering = Boolean(state || year);

  return (
    <>
      <PageHead onCreate={openCreate} />

      <div className="toolbar">
        <select
          className="toolbar-select"
          aria-label="Filter by state"
          value={state}
          onChange={(event) => setState(event.target.value)}
        >
          <option value="">All states</option>
          {(data?.states ?? []).map((option) => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </select>
        <select
          className="toolbar-select"
          aria-label="Filter by year"
          value={year}
          onChange={(event) => setYear(event.target.value)}
        >
          <option value="">All years</option>
          {(data?.years ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <div className="spacer" />
        <span className="hint">
          Working days are Monday to Friday; no holiday calendar. Offsets are set in Configuration.
        </span>
      </div>

      {cycles.isLoading ? null : items.length === 0 ? (
        <StateCard
          icon={<CalendarDays aria-hidden="true" />}
          title={filtering ? 'No cycles match these filters' : 'No cycles yet'}
        >
          {!filtering && (
            <p>Each month Finance creates one cycle from its closing month and year.</p>
          )}
          {!filtering && openCreate && (
            <div className="empty-actions">
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                <Plus aria-hidden="true" /> New cycle
              </button>
            </div>
          )}
        </StateCard>
      ) : (
        <div className="card">
          <div className="table-scroll">
            <table className="data zebra cycle-table">
              <thead>
                <tr>
                  <th scope="col">Cycle</th>
                  <th scope="col">Label</th>
                  <th scope="col">State</th>
                  <th scope="col">Closing month</th>
                  {MILESTONE_COLUMNS.map((column) => (
                    <th scope="col" key={column.key}>
                      {column.label}{' '}
                      <span className="cycle-wd-tag">{columnTag(items, column.key)}</span>
                    </th>
                  ))}
                  <th scope="col">Previous cycle</th>
                </tr>
              </thead>
              <tbody>
                {items.map((cycle) => (
                  <CycleRow key={cycle.id} cycle={cycle} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && <NewCycleDialog onClose={() => setCreating(false)} />}
    </>
  );
}
